///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { GtthReactiveInputBase } from './input-base';
import { GtthInputTypesEnum } from './input-types.enum';
import { GtthKeyValueDTO } from '../../generated/shared-types.model';
import { GtthInputControlService } from './input-control.service';
import { Subscription } from 'rxjs';
import { _cloneDeep } from '../utils';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { SuiTranslatePipe } from '@siklu/sui-lib';

@Component({
    selector: 'winde-reactive-input',
    templateUrl: './reactive-form-input.component.html',
    styleUrls: ['./reactive-form-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SuiTranslatePipe, ReactiveFormsModule, NgClass, NgTemplateOutlet]
})
export class GtthReactiveFormInputComponent implements OnInit, OnDestroy {
    public inputTypesEnum = GtthInputTypesEnum;

    public inputToShow: GtthReactiveInputBase<any>;
    public inputControl: AbstractControl;
    public showValue: any;
    public formGroupToShow: FormGroup;
    public showValidationMessage: boolean;
    public inputId: string;
    @Input() form: FormGroup;
    private inpChangeSubscriber: Subscription;
    private formSubmitSubscriber: Subscription;

    constructor(
        private readonly inputControlService: GtthInputControlService,
        private readonly changeDetector: ChangeDetectorRef
    ) {}

    @Input() set input(value: GtthReactiveInputBase<any>) {
        this.inputToShow = value;
    }

    ngOnInit() {
        this.formGroupToShow = this.inputControlService.getInpFormGroup(this.form, this.inputToShow) as FormGroup;
        this.inputControl = this.inputControlService.getInputControl(this.form, this.inputToShow);
        this.inputId = this.inputToShow.formGroupName ? this.inputToShow.formGroupName + '.' + this.inputToShow.key : this.inputToShow.key;

        if (this.inputToShow.hideInput) {
            this.showValue = this.searchValueByKey();
        }

        this.inpChangeSubscriber = this.inputControlService.inputChange$.subscribe((input: GtthReactiveInputBase<unknown>) => {
            if (this.inputToShow.key !== input.key) {
                return;
            }
            const inputControl = this.form.get(this.inputToShow.originKey);
            if (inputControl.disabled !== this.inputToShow.disabled) {
                if (this.inputToShow.disabled) {
                    inputControl.disable();
                } else {
                    inputControl.enable();
                }
            }
            this.changeDetector.markForCheck();
        });

        this.formSubmitSubscriber = this.inputControlService.formSubmitSource$.subscribe(() => {
            this.showValidationMessage = true;
            this.changeDetector.markForCheck();
        });
    }

    ngOnDestroy() {
        if (this.inpChangeSubscriber) {
            this.inpChangeSubscriber.unsubscribe();
            this.inpChangeSubscriber = undefined;
        }
        if (this.formSubmitSubscriber) {
            this.formSubmitSubscriber.unsubscribe();
            this.formSubmitSubscriber = undefined;
        }
    }

    public searchValueByKey() {
        if (!this.inputToShow.options) {
            return '';
        }

        const showValue = this.inputToShow.options.find((option: GtthKeyValueDTO) => {
            return option.key === this.inputToShow.value;
        });

        return showValue.value;
    }

    public getValidationErrors(errors: ValidationErrors, inputToShow: GtthReactiveInputBase<any>): { query: string; args: any[] }[] {
        const errorMsgs = [];
        const errorPrefix = 'error.validation.field.';

        if (errors) {
            Object.keys(errors).forEach((key) => {
                const inputConfig = _cloneDeep(inputToShow);
                Object.keys(errors[key]).forEach((errorKeys) => (inputConfig[errorKeys] = errors[key][errorKeys]));

                errorMsgs.push({
                    query: errorPrefix + key,
                    args: inputConfig
                });
            });
        }

        return errorMsgs;
    }
}
